import { useEffect, useState } from 'react';

import {
  DOUBLE_GATE_BROADCAST_CHANNEL,
  DOUBLE_GATE_MESSAGE_TYPE,
  ENTITY_TYPE,
} from '../utils/constants';
import { getCookie } from '../utils/helper';
import { DoubleGateMessage, Enum } from '../utils/types';
import { useBroadcastChannel } from './useBroadcastChannel';
import useIsTabActive from './useIsTabActive';

/**
 * 1. Request the parent details
 * 2. Listens to the parent details
 * 3. Close the connection
 */
export const useLeadFormPrefillChildListener = (
  entityId: string,
  entityType: Enum<typeof ENTITY_TYPE>,
) => {
  const tabHasFocus = useIsTabActive();

  const [parentDetails, setParentDetails] = useState<{
    parentLeadUuid?: string;
    parentEntityId?: string;
    parentEntityType?: Enum<typeof ENTITY_TYPE>;
  }>({});

  const serviceParentResponse = (
    response: DoubleGateMessage,
    broadcastChannel: BroadcastChannel,
  ) => {
    /** Must check type. Dont remove ? */
    if (response?.type === DOUBLE_GATE_MESSAGE_TYPE.RESPONSE_PARENT_LEAD_DETAILS) {
      const {
        requestId,
        leadUuid: parentLeadUuid,
        entityId: parentEntityId,
        entityType: parentEntityType,
      } = response;
      if (requestId === entityId) {
        setParentDetails({ parentLeadUuid, parentEntityId, parentEntityType });
        broadcastChannel.close();
      }
    }
  };

  const { emitMessage, isOpen } = useBroadcastChannel<DoubleGateMessage>(
    DOUBLE_GATE_BROADCAST_CHANNEL,
    serviceParentResponse,
  );

  useEffect(() => {
    if (!isOpen || !tabHasFocus || Object.keys(parentDetails).length) return;
    emitMessage({
      type: DOUBLE_GATE_MESSAGE_TYPE.REQUEST_PARENT_LEAD_DETAILS,
      entityId,
      entityType,
      requestId: entityId,
    });
    /** Dont add emitMessage to deps */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, isOpen, tabHasFocus, parentDetails, entityType]);

  return parentDetails;
};

/**
 * 1. Listens to child request for parent details
 * 2. Responds with parent leadUuid, entityId and entityType
 */
export const useLeadFormPrefillParentListener = (
  entityId: string,
  entityType: Enum<typeof ENTITY_TYPE>,
) => {
  const tabHasFocus = useIsTabActive();
  useBroadcastChannel(
    DOUBLE_GATE_BROADCAST_CHANNEL,
    (message: DoubleGateMessage, channel: BroadcastChannel) => {
      /** Must verify type and focus. Dont remove ? */
      if (tabHasFocus && message?.type === DOUBLE_GATE_MESSAGE_TYPE.REQUEST_PARENT_LEAD_DETAILS) {
        const leadUuid = getCookie(entityId);
        if (!leadUuid) return;
        channel.postMessage({
          leadUuid,
          entityId,
          entityType,
          type: DOUBLE_GATE_MESSAGE_TYPE.RESPONSE_PARENT_LEAD_DETAILS,
          requestId: message.requestId,
        });
      }
    },
  );
};
